import { saveWaitlistEntry, updateWaitlistAnswers, DuplicateEmailError } from './supabase.js';

declare const mixpanel: { track: (event: string, props?: Record<string, unknown>) => void; identify: (id: string) => void; people: { set: (props: Record<string, unknown>) => void } };
const mp = (event: string, props?: Record<string, unknown>) => mixpanel.track(event, props);

// ─── State ────────────────────────────────────────────────────────────────────

let capturedEmail = '';
let step2Answers: { appointments_per_week?: string; preferred_channel?: string } = {};

// ─── UTM ──────────────────────────────────────────────────────────────────────

const UTM_KEY = 'bookme_utm';

function captureUtm(): void {
  const p = new URLSearchParams(window.location.search);
  if (p.get('utm_source')) {
    sessionStorage.setItem(UTM_KEY, JSON.stringify({
      source:       p.get('utm_source')   ?? 'direct',
      utm_medium:   p.get('utm_medium')   ?? undefined,
      utm_campaign: p.get('utm_campaign') ?? undefined,
    }));
  }
}

function getUtm(): { source: string; utm_medium?: string; utm_campaign?: string } {
  const stored = sessionStorage.getItem(UTM_KEY);
  if (stored) return JSON.parse(stored);
  return { source: 'direct' };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function el<T extends HTMLElement>(id: string): T {
  const elem = document.getElementById(id);
  if (!elem) throw new Error(`Element #${id} not found`);
  return elem as T;
}

function query<T extends HTMLElement>(selector: string): T | null {
  return document.querySelector<T>(selector);
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function flashInvalid(input: HTMLInputElement): void {
  input.style.borderColor = '#c0392b';
  setTimeout(() => (input.style.borderColor = ''), 1500);
}

function setLoading(btn: HTMLButtonElement, loading: boolean, label: string): void {
  btn.disabled = loading;
  btn.textContent = loading ? 'Salvataggio…' : label;
}

// ─── Modal ────────────────────────────────────────────────────────────────────

function openModal(): void {
  mp('waitlist_modal_opened');
  step2Answers = {};
  // reset form state
  el<HTMLInputElement>('modalEmail').value = '';
  el<HTMLInputElement>('consentCheck').checked = true;
  el('consentError').style.display = 'none';
  el('step1Error').style.display = 'none';
  el('step1Duplicate').style.display = 'none';
  el('step1Btn').style.display = 'block';
  el('step1CloseBtn').style.display = 'none';
  el('step3Error').style.display = 'none';
  document.querySelectorAll<HTMLElement>('.pill').forEach((p) => p.classList.remove('selected'));
  el<HTMLSelectElement>('q_appointments').value = '';
  el<HTMLInputElement>('q_note').value = '';

  el('overlay').classList.add('active');
  document.body.style.overflow = 'hidden';
  showStep(1);
}

function closeModal(): void {
  el('overlay').classList.remove('active');
  document.body.style.overflow = '';
}

function showStep(n: 1 | 2 | 3 | 4): void {
  currentStep = n;
  document.querySelectorAll<HTMLElement>('.modal-step').forEach((s) =>
    s.classList.remove('active'),
  );
  el(`step${n}`).classList.add('active');
}

// ─── Pill selection ───────────────────────────────────────────────────────────

function selectPill(pill: HTMLElement, groupId: string): void {
  document.querySelectorAll<HTMLElement>(`#${groupId} .pill`).forEach((p) =>
    p.classList.remove('selected'),
  );
  pill.classList.add('selected');
}

// ─── Step handlers ────────────────────────────────────────────────────────────

async function submitStep1(): Promise<void> {
  const input = el<HTMLInputElement>('modalEmail');
  const email = input.value.trim();
  if (!isValidEmail(email)) {
    flashInvalid(input);
    return;
  }
  if (!el<HTMLInputElement>('consentCheck').checked) {
    el('consentError').style.display = 'block';
    return;
  }
  el('consentError').style.display = 'none';

  const btn = el<HTMLButtonElement>('step1Btn');
  setLoading(btn, true, 'Continua →');

  try {
    await saveWaitlistEntry({ email, ...getUtm() });
    capturedEmail = email;
    mixpanel.identify(email);
    mixpanel.people.set({ $email: email, ...getUtm() });
    mp('waitlist_email_submitted', getUtm());
    showStep(2);
  } catch (err) {
    if (err instanceof DuplicateEmailError) {
      el('step1Duplicate').style.display = 'block';
      el('step1Btn').style.display = 'none';
      el('step1CloseBtn').style.display = 'block';
    } else {
      console.error('Supabase error:', err);
      el('step1Error').style.display = 'block';
    }
  } finally {
    setLoading(btn, false, 'Continua →');
  }
}

function submitStep2(): void {
  const appts = el<HTMLSelectElement>('q_appointments').value;
  const channel = query<HTMLElement>('#q_channel .selected');

  step2Answers = {
    appointments_per_week: appts || undefined,
    preferred_channel: channel?.textContent?.trim() || undefined,
  };

  mp('waitlist_step2_completed', step2Answers);
  showStep(3);
}

async function finishStep3(skip: boolean): Promise<void> {
  const answers: Parameters<typeof updateWaitlistAnswers>[1] = {
    ...step2Answers,
  };

  if (!skip) {
    const price = query<HTMLElement>('#q_price .selected');
    if (price) answers.monthly_budget = price.textContent?.trim();
    const note = el<HTMLInputElement>('q_note').value.trim();
    if (note) answers.note = note;
  }

  // Only update if there's anything to save
  const hasAnswers = Object.values(answers).some(Boolean);
  if (!hasAnswers) {
    showStep(4);
    return;
  }

  const btn = el<HTMLButtonElement>('step3Btn');
  setLoading(btn, true, 'Invia e sono dentro →');

  try {
    await updateWaitlistAnswers(capturedEmail, answers);
    mp('waitlist_completed', { skipped: skip, ...answers });
    showStep(4);
  } catch (err) {
    console.error('Supabase error:', err);
    el('step3Error').style.display = 'block';
  } finally {
    setLoading(btn, false, 'Invia e sono dentro →');
  }
}

// ─── Scroll reveal ────────────────────────────────────────────────────────────

function initHeroRotator(): void {
  const rotator = document.getElementById('heroRotator');
  if (!rotator) return;
  const professions = ['psicologi', 'osteopati', 'medici', 'fisioterapisti', 'nutrizionisti', 'dentisti', 'logopedisti'];
  let i = 0;
  setInterval(() => {
    rotator.classList.add('out');
    setTimeout(() => {
      i = (i + 1) % professions.length;
      rotator.textContent = professions[i];
      rotator.classList.remove('out');
    }, 350);
  }, 2200);
}

function initScrollReveal(): void {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          observer.unobserve(e.target);
        }
      });
    },
    { threshold: 0.12 },
  );
  document.querySelectorAll('.reveal').forEach((elem) => observer.observe(elem));
}

// ─── Event wiring ─────────────────────────────────────────────────────────────

// ─── Current step tracker (for modal_abandoned) ───────────────────────────────

let currentStep = 1;

function init(): void {
  captureUtm();

  // Page view
  mp('page_viewed', getUtm());

  // All CTA buttons open the modal — track which one
  document.querySelectorAll<HTMLElement>('[data-open-modal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const label = btn.dataset.ctaLabel ?? btn.textContent?.trim() ?? 'unknown';
      mp('cta_clicked', { label });
      openModal();
    });
  });

  // Close on overlay click — track abandonment
  el('overlay').addEventListener('click', (e) => {
    if (e.target === el('overlay')) {
      if (currentStep < 4) mp('modal_abandoned', { step: currentStep });
      closeModal();
    }
  });

  // Step 1: email submit
  el('step1Btn').addEventListener('click', submitStep1);
  el('step1CloseBtn').addEventListener('click', closeModal);
  el('modalEmail').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submitStep1();
  });
  el('consentCheck').addEventListener('change', () => {
    if (el<HTMLInputElement>('consentCheck').checked) {
      el('consentError').style.display = 'none';
    }
  });

  // Step 2: continue (save answers) or skip
  el('step2Btn').addEventListener('click', submitStep2);
  el('step2Skip').addEventListener('click', () => { mp('waitlist_step2_skipped'); showStep(3); });

  // Step 3: submit or skip
  el('step3Btn').addEventListener('click', () => finishStep3(false));
  el('step3Skip').addEventListener('click', () => finishStep3(true));

  // Pills
  document.querySelectorAll<HTMLElement>('.pill').forEach((pill) => {
    pill.addEventListener('click', () => {
      const group = pill.closest('.pill-group');
      if (group?.id) selectPill(pill, group.id);
    });
  });

  // Share buttons
  const baseUrl = window.location.href.split('?')[0];
  const pageUrl = `${baseUrl}?utm_source=referral&utm_medium=share&utm_campaign=waitlist`;

  el('copyLinkBtn').addEventListener('click', () => {
    navigator.clipboard.writeText(pageUrl).then(() => {
      mp('referral_link_copied');
      const btn = el('copyLinkBtn');
      btn.textContent = 'Link copiato ✓';
      setTimeout(() => (btn.textContent = 'Copia link'), 2000);
    });
  });

  el<HTMLAnchorElement>('whatsappBtn').addEventListener('click', () => mp('referral_whatsapp_clicked'));
  el<HTMLAnchorElement>('whatsappBtn').href =
    `https://wa.me/?text=${encodeURIComponent('Ho trovato questo strumento interessante per gestire l\'agenda: ' + pageUrl)}`;

  // FAQ accordion
  document.querySelectorAll<HTMLButtonElement>('.faq-q').forEach((btn) => {
    btn.addEventListener('click', () => {
      const answer = btn.nextElementSibling as HTMLElement;
      const isOpen = btn.getAttribute('aria-expanded') === 'true';

      // Close all others
      document.querySelectorAll<HTMLButtonElement>('.faq-q').forEach((b) => {
        b.setAttribute('aria-expanded', 'false');
        (b.nextElementSibling as HTMLElement).style.maxHeight = '0';
      });

      if (!isOpen) {
        btn.setAttribute('aria-expanded', 'true');
        answer.style.maxHeight = answer.scrollHeight + 'px';
        mp('faq_question_opened', { question: btn.textContent?.trim() });
      }
    });
  });

  // Section views
  const sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const label = (e.target as HTMLElement).dataset.section;
          if (label) mp('section_viewed', { section: label });
          sectionObserver.unobserve(e.target);
        }
      });
    },
    { threshold: 0.3 },
  );
  document.querySelectorAll<HTMLElement>('[data-section]').forEach((s) =>
    sectionObserver.observe(s),
  );

  initScrollReveal();
  initHeroRotator();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
